/**
 * PublishPreviewDialog
 *
 * Zeigt vor jedem Live-Post eine Vorschau mit Inhalt & Kanal.
 * Der Nutzer muss explizit „Jetzt veröffentlichen" bestätigen.
 *
 * Props:
 *   post       — Post-Objekt aus dem Kalender (alle Felder)
 *   onClose    — Callback zum Schließen (Abbrechen)
 *   onSuccess  — Callback nach erfolgreichem Publish (erhält API-Resultat)
 *   mode       — "publish" (Default, unverändertes Bestandsverhalten)
 *              | "preview" (reines Ansehen: kein Veröffentlichen-Button, kein
 *                „wird sofort veröffentlicht"-Hinweis, dafür Datum + Uhrzeit im Kopf)
 */

import { useState, useEffect } from "react";
import axios from "axios";
import {
  X, Send, Loader2, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  Facebook, Instagram, Linkedin
} from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

// Relative Medienpfade (/api/media/file/…?t=…) gegen den Backend-Host auflösen —
// dieselbe Regel wie im übrigen Frontend. Absolute URLs (https:, data:, blob:)
// bleiben unangetastet.
const mediaSrc = (url) =>
  !url ? "" : /^(https?:|data:|blob:)/i.test(url) ? url : `${BACKEND}${url}`;

// ── Plattform-Konfiguration ────────────────────────────────────────────────
const PLATFORM_CFG = {
  facebook: {
    label:   "Facebook",
    headerCls: "bg-blue-600",
    btnCls:  "bg-blue-600 hover:bg-blue-700 text-white",
    Icon:    Facebook,
  },
  instagram: {
    label:   "Instagram",
    headerCls: "bg-gradient-to-tr from-purple-600 to-pink-500",
    btnCls:  "bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white",
    Icon:    Instagram,
  },
  linkedin: {
    label:   "LinkedIn",
    headerCls: "bg-sky-700",
    btnCls:  "bg-sky-700 hover:bg-sky-800 text-white",
    Icon:    Linkedin,
  },
  tiktok: {
    label:   "TikTok",
    headerCls: "bg-gray-900",
    btnCls:  "bg-gray-900 hover:bg-black text-white",
    Icon:    null,
  },
  google_business: {
    label:   "Google Business",
    headerCls: "bg-orange-500",
    btnCls:  "bg-orange-500 hover:bg-orange-600 text-white",
    Icon:    null,
  },
  pinterest: {
    label:   "Pinterest",
    headerCls: "bg-red-600",
    btnCls:  "bg-red-600 hover:bg-red-700 text-white",
    Icon:    null,
  },
};

// Unbekannte Plattform: neutraler Kopf mit dem echten Namen statt Instagram-Optik
// (sonst stand bei einem Pinterest-Beitrag „Instagram“ im Dialog).
const fallbackCfg = (platform) => ({
  label:   platform ? platform.charAt(0).toUpperCase() + platform.slice(1).replace(/_/g, " ") : "Social Media",
  headerCls: "bg-gray-700",
  btnCls:  "bg-gray-700 hover:bg-gray-800 text-white",
  Icon:    null,
});

// ── Karten-Rollen (Vertrag R1) ─────────────────────────────────────────────
// Kundenorientiertes Rollen-Vokabular (übersetzt via t()). Schlüssel exakt wie das
// Backend sie in card_roles[] liefert; Werte = die sichtbaren Labels.
const cardRoleLabels = (t) => ({
  hook:    "Hook",
  context: t("Kontext", "Context"),
  body:    t("Folie", "Slide"),
  summary: t("Fazit", "Summary"),
  cta:     "Call-to-Action",
  single:  t("Bild", "Image"),
});

// Rolle + Index → Label (R4). body-Karten werden fortlaufend NUR über die
// body-Karten nummeriert („Folie 1", „Folie 2", …). Fehlt card_roles (Altposts),
// Rückfall je Karte auf „N/Gesamt" — das heutige Verhalten, kein leeres Label.
const buildCardLabels = (cardRoles, total, t) => {
  const roles = Array.isArray(cardRoles) ? cardRoles : [];
  const roleLabels = cardRoleLabels(t);
  let bodyN = 0;
  return Array.from({ length: total }, (_, i) => {
    const role = roles[i];
    if (role === "body") { bodyN += 1; return t(`Folie ${bodyN}`, `Slide ${bodyN}`); }
    return roleLabels[role] || `${i + 1}/${total}`;
  });
};

// Datum + Uhrzeit für den Vorschau-Kopf, z.B. „Mo., 28. Juli 2026 · 12:00".
// Der Kalender liefert scheduled_date als "YYYY-MM-DD" — mit "T00:00:00" geparst,
// damit der Browser es als LOKALES Datum liest (sonst UTC → ein Tag daneben).
// lang steuert das Anzeige-Gebietsschema (de-DE / en-US) passend zur UI-Sprache.
const formatWhen = (post, lang) => {
  const dateStr = post?.scheduled_date || post?.date || "";
  const timeStr = (post?.scheduled_time || post?.time || "").trim();
  let datePart = "";
  if (dateStr) {
    try {
      datePart = new Date(`${dateStr}T00:00:00`).toLocaleDateString(lang === "en" ? "en-US" : "de-DE", {
        weekday: "short", day: "numeric", month: "long", year: "numeric",
      });
    } catch { datePart = dateStr; }
  }
  return [datePart, timeStr].filter(Boolean).join(" · ");
};

// ── Hauptkomponente ────────────────────────────────────────────────────────
export default function PublishPreviewDialog({ post, onClose, onSuccess, mode = "publish" }) {
  const { t, language } = useLanguage();
  const [publishing, setPublishing] = useState(false);
  const [error, setError]           = useState(null);   // user-facing Fehlermeldung
  const [errorCode, setErrorCode]   = useState(null);   // technischer error_code für Diagnose
  const [done, setDone]             = useState(false);
  const [cardIndex, setCardIndex]   = useState(0);      // aktuell gezeigte Karussell-Karte
  // Sichtungs-Gate: im publish-Modus darf erst veröffentlicht werden, wenn ALLE
  // Karussell-Folien einmal gezeigt wurden. Karte 0 gilt sofort als gesehen.
  const [seenCards, setSeenCards]   = useState(() => new Set([0]));

  const isPreview = mode === "preview";

  const platform  = post?.platform || post?.channel || "instagram";
  const pCfg      = PLATFORM_CFG[platform] || fallbackCfg(platform);
  const PIcon     = pCfg.Icon;

  const hook      = (post?.hook_full || post?.hook || "").trim();
  // content/body zusätzlich: simple_flow-Posts legen den Text dort ab und hätten
  // sonst eine leere Vorschau.
  const core      = (post?.core_content || post?.text_content || post?.content || post?.body || "").trim();
  const rawHt     = post?.hashtags || [];
  const hashtags  = Array.isArray(rawHt)
    ? rawHt.slice(0, 8).join(" ")
    : String(rawHt);
  // Format-Labels (übersetzt via t()) — Nachschlagetabelle liegt hier in der
  // Komponente, damit sie Zugriff auf t() hat.
  const FORMAT_LABELS = {
    post:      t("Beitrag", "Post"),
    carousel:  t("Karussell", "Carousel"),
    video:     t("Video", "Video"),
    reel:      t("Reel", "Reel"),
    story:     t("Story", "Story"),
  };
  const format    = FORMAT_LABELS[post?.content_type] || t("Beitrag", "Post");
  const imageUrl  = post?.image_url || post?.media_url;
  const videoUrl  = post?.video_url;
  const isTikTok  = platform === "tiktok";
  const postId    = post?.id;
  const when      = isPreview ? formatWhen(post, language) : "";

  // Karussell-Karten: card_urls[] wenn vorhanden, sonst das Einzelbild als 1er-Liste.
  // card_alts[] liefert die deutschen Alt-Texte (Barrierefreiheit; Bluesky verlangt sie).
  const cardUrls = Array.isArray(post?.card_urls) && post.card_urls.length
    ? post.card_urls
    : (imageUrl ? [imageUrl] : []);
  const cardAlts = Array.isArray(post?.card_alts) ? post.card_alts : [];
  // Blättern immer bei mehr als einer Karte — auch im publish-Modus. Früher zeigte
  // publish nur das Einzelbild; niemand soll veröffentlichen, ohne alle Folien
  // gesehen zu haben (Sichtungs-Gate weiter unten). Der Einzelbild-Fall bleibt schlicht.
  const showCarousel = cardUrls.length > 1;
  const safeIndex = Math.min(cardIndex, Math.max(cardUrls.length - 1, 0));
  const altOf = (i) => (typeof cardAlts[i] === "string" && cardAlts[i].trim())
    ? cardAlts[i]
    : t("Beitragsbild", "Post image");

  // Rollen-Labels je Karte (Vertrag R1/R4). card_roles ist indexgleich zu card_urls.
  // Fehlt es (Altposts), fällt cardLabelOf auf reine Nummerierung zurück.
  const cardRoles    = Array.isArray(post?.card_roles) ? post.card_roles : [];
  const hasRoleLabels = cardRoles.length > 0;
  const cardLabels   = buildCardLabels(cardRoles, cardUrls.length, t);
  const cardLabelOf  = (i) => cardLabels[i] || `${i + 1}/${cardUrls.length}`;

  const goPrev = () => setCardIndex((i) => (i <= 0 ? cardUrls.length - 1 : i - 1));
  const goNext = () => setCardIndex((i) => (i >= cardUrls.length - 1 ? 0 : i + 1));

  // Tastatur links/rechts blättert — nur solange das Karussell aktiv ist.
  useEffect(() => {
    if (!showCarousel) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCarousel, cardUrls.length]);

  // Aktuell gezeigte Karte als „gesehen" vormerken — speist das Sichtungs-Gate.
  useEffect(() => {
    setSeenCards((prev) => (prev.has(safeIndex) ? prev : new Set(prev).add(safeIndex)));
  }, [safeIndex]);

  // Wischgeste (Touch): horizontaler Swipe ab ~40px blättert.
  const [touchX, setTouchX] = useState(null);
  const onTouchStart = (e) => setTouchX(e.touches?.[0]?.clientX ?? null);
  const onTouchEnd = (e) => {
    if (touchX == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? touchX) - touchX;
    if (dx > 40) goPrev();
    else if (dx < -40) goNext();
    setTouchX(null);
  };

  // Veröffentlichen darf nur für passende Status aufgerufen werden —
  // die Seite filtert bereits, aber ein zweiter Check schadet nicht.
  const PUBLISHABLE = ["approved", "ready", "scheduled", "done", "failed"];
  const canPublish  = PUBLISHABLE.includes(post?.status) && !done;
  const tiktokMissingVideo = isTikTok && !videoUrl;
  // Nur ein echtes Karussell wird gated; Einzelbild/Video bleiben ungehindert.
  const allCardsSeen = !showCarousel || seenCards.size >= cardUrls.length;

  const handlePublish = async () => {
    if (!canPublish || tiktokMissingVideo || publishing || !allCardsSeen) return;
    setPublishing(true);
    setError(null);
    setErrorCode(null);

    try {
      const res = await axios.post(`${API}/publish/now`, {
        post_id: postId,
        platform,
      });

      if (res.data.success) {
        setDone(true);
        // kurz Erfolgs-State zeigen, dann schließen & Callback
        setTimeout(() => {
          onSuccess(res.data);
        }, 900);
      } else {
        // user_message priorisieren (menschlich lesbar), error als Fallback
        const msg = res.data.user_message || res.data.error || t("Veröffentlichung fehlgeschlagen.", "Publishing failed.");
        setError(msg);
        setErrorCode(res.data.error_code || null);
      }
    } catch (err) {
      // setError rendert direkt als React-Child → niemals ein Objekt/Array (422-detail)
      // setzen (sonst Crash "Objects are not valid as a React child"). Nur String zeigen.
      const d = err.response?.data?.user_message || err.response?.data?.detail;
      setError((typeof d === "string" && d.trim())
        ? d
        : t("Veröffentlichung fehlgeschlagen. Bitte versuche es später erneut.", "Publishing failed. Please try again later."));
    } finally {
      setPublishing(false);
    }
  };

  // Backdrop-Klick schließt nur wenn nicht am Publizieren
  const handleBackdrop = () => {
    if (!publishing) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleBackdrop}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg overflow-hidden"
        style={{ animation: "fadeIn 0.18s ease" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className={`${pCfg.headerCls} px-5 py-4 flex items-center justify-between`}>
          {isPreview ? (
            <div className="text-white min-w-0">
              <div className="flex items-center gap-2.5">
                {PIcon && <PIcon size={20} />}
                {!PIcon && (
                  <span className="text-lg">
                    {platform === "tiktok" ? "🎵" : platform === "pinterest" ? "📌" : "🌐"}
                  </span>
                )}
                <span className="font-heading font-bold text-base leading-tight">
                  {t("Vorschau", "Preview")}
                </span>
                <span className="text-white/60 text-sm font-normal">— {pCfg.label} · {format}</span>
              </div>
              {when && (
                <p className="text-white/75 text-xs mt-1 pl-[30px] truncate">{when}</p>
              )}
            </div>
          ) : (
          <div className="flex items-center gap-2.5 text-white">
            {PIcon && <PIcon size={20} />}
            {!PIcon && (
              <span className="text-lg">
                {platform === "tiktok" ? "🎵" : platform === "pinterest" ? "📌" : "🌐"}
              </span>
            )}
            <span className="font-heading font-bold text-base leading-tight">
              {pCfg.label}
            </span>
            <span className="text-white/60 text-sm font-normal">— {format}</span>
          </div>
          )}
          <button
            onClick={onClose}
            disabled={publishing}
            className="text-white/60 hover:text-white transition-colors disabled:opacity-30"
            title={isPreview ? t("Schließen", "Close") : t("Abbrechen", "Cancel")}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="p-5 space-y-4">

          {/* Hinweis — nur im Publish-Modus: in der reinen Vorschau passiert nichts,
              die Warnung wäre schlicht falsch. */}
          {!isPreview && (
          <p className="text-xs text-stone-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
            {t("Dieser Beitrag wird", "This post will be published")}{" "}
            <strong className="text-amber-700">{t("sofort", "immediately")}</strong>{t(" auf", " on")}{" "}
            <strong className="text-amber-700">{pCfg.label}</strong>{t(" veröffentlicht — bitte Inhalt prüfen.", " — please review the content.")}
          </p>
          )}

          {/* Medien-Vorschau */}
          {videoUrl ? (
            <div className="rounded-xl overflow-hidden border border-stone-200 aspect-video bg-stone-900 flex items-center justify-center">
              <video
                src={mediaSrc(videoUrl)}
                className="w-full h-full object-contain"
                muted
                playsInline
              />
            </div>
          ) : showCarousel ? (
            /* Karussell: durchblätterbar per Pfeil, Wisch oder Tastatur links/rechts. */
            <div
              className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-50 select-none"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={mediaSrc(cardUrls[safeIndex])}
                alt={altOf(safeIndex)}
                className="w-full max-h-72 object-contain bg-white"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
              <button
                type="button"
                onClick={goPrev}
                aria-label={t("Vorherige Karte", "Previous slide")}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/65 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label={t("Nächste Karte", "Next slide")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/45 text-white hover:bg-black/65 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
              {/* Rollen-Label + Zähler; aria-live meldet den Wechsel an Screenreader. */}
              <span
                aria-live="polite"
                className="absolute top-2 right-2 text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/45 text-white"
              >
                {hasRoleLabels ? (
                  <>{cardLabelOf(safeIndex)} · <span className="tabular-nums">{safeIndex + 1}/{cardUrls.length}</span></>
                ) : (
                  <span className="tabular-nums">{safeIndex + 1} / {cardUrls.length}</span>
                )}
              </span>
              {/* Punkt-Indikator */}
              <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1.5">
                {cardUrls.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCardIndex(i)}
                    aria-label={hasRoleLabels ? t(`Zu ${cardLabelOf(i)}`, `Go to ${cardLabelOf(i)}`) : t(`Zu Karte ${i + 1}`, `Go to slide ${i + 1}`)}
                    aria-current={i === safeIndex}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === safeIndex ? "bg-white" : "bg-white/45 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : cardUrls.length ? (
            <div className="rounded-xl overflow-hidden border border-stone-200 bg-stone-50">
              <img
                src={mediaSrc(cardUrls[0])}
                alt={altOf(0)}
                className="w-full max-h-52 object-cover"
                onError={(e) => { e.target.closest("div").style.display = "none"; }}
              />
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 h-24 flex items-center justify-center text-stone-400 text-sm">
              {isTikTok ? t("🎬 Kein Video hinterlegt", "🎬 No video added") : t("🖼️ Kein Bild hinterlegt", "🖼️ No image added")}
            </div>
          )}

          {/* Karussell-Onboarding — dezenter Playbook-Hinweis, nur bei echtem Blättern. */}
          {showCarousel && (
            <p className="text-[11px] text-stone-400 leading-relaxed -mt-1">
              {t(
                "Karussell: Hook zuerst, eine Idee pro Folie, klarer Call-to-Action am Ende.",
                "Carousel: hook first, one idea per slide, a clear call to action at the end.",
              )}
            </p>
          )}

          {/* Sichtungs-Gate-Hinweis — nur im publish-Modus, solange nicht alle Folien gesehen. */}
          {!isPreview && showCarousel && !allCardsSeen && (
            <div className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-700">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-sky-500" />
              <span>
                {t(
                  `Bitte blättere einmal durch alle ${cardUrls.length} Folien, bevor du veröffentlichst.`,
                  `Please flip through all ${cardUrls.length} slides before publishing.`,
                )}
              </span>
            </div>
          )}

          {/* Caption-Vorschau */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 max-h-44 overflow-y-auto space-y-2">
            {hook ? (
              <p className="font-semibold text-stone-800 text-sm leading-snug">{hook}</p>
            ) : (
              <p className="text-stone-400 text-xs italic">{t("Kein Hook / Titel vorhanden", "No hook / title yet")}</p>
            )}
            {core && (
              <p className="text-stone-600 text-xs leading-relaxed whitespace-pre-line">
                {core.length > 450 ? core.slice(0, 450) + "…" : core}
              </p>
            )}
            {hashtags && (
              <p className="text-blue-500 text-xs break-words">
                {hashtags.length > 160 ? hashtags.slice(0, 160) + "…" : hashtags}
              </p>
            )}
          </div>

          {/* TikTok-Warnung */}
          {tiktokMissingVideo && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-500" />
              <span>
                {t(
                  "TikTok-Posts benötigen ein Video. Bitte zuerst ein Video für diesen Post generieren.",
                  "TikTok posts require a video. Please generate a video for this post first.",
                )}
              </span>
            </div>
          )}

          {/* Fehler-Anzeige */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-red-500" />
              <div className="space-y-1">
                <span className="block">{error}</span>
                {errorCode === "not_connected" && (
                  <span className="block text-red-500 font-medium">
                    {t("Verbinde", "Connect")} {pCfg.label} {t("unter", "under")}{" "}
                    <a href="/kanale" className="underline">{t("Kanäle verwalten", "Manage channels")}</a>.
                  </span>
                )}
                {errorCode === "platform_unreachable" && (
                  <span className="block text-red-400">
                    {t(
                      "Die Plattform war nicht erreichbar — bitte in 5 Minuten erneut versuchen.",
                      "The platform was unreachable — please try again in 5 minutes.",
                    )}
                  </span>
                )}
                {errorCode && !["not_connected", "platform_unreachable"].includes(errorCode) && (
                  <span className="block text-red-400 opacity-60">
                    {t("Code", "Code")}: {errorCode}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Erfolgs-State */}
          {done && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 font-medium">
              <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
              {t("Erfolgreich veröffentlicht!", "Successfully published!")}
            </div>
          )}
        </div>

        {/* ── Aktionen ── */}
        {isPreview ? (
        <div className="px-5 pb-5 flex gap-3 justify-end border-t border-stone-100 pt-4">
          <button
            onClick={onClose}
            data-testid="preview-close-btn"
            className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-stone-100 text-stone-700 hover:bg-stone-200 transition-colors"
          >
            {t("Schließen", "Close")}
          </button>
        </div>
        ) : (
        <div className="px-5 pb-5 flex gap-3 justify-end border-t border-stone-100 pt-4">
          <button
            onClick={onClose}
            disabled={publishing || done}
            className="px-4 py-2 text-sm text-stone-500 hover:text-stone-700 transition-colors disabled:opacity-40"
          >
            {t("Abbrechen", "Cancel")}
          </button>
          <button
            onClick={handlePublish}
            disabled={publishing || done || tiktokMissingVideo || !canPublish || !allCardsSeen}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pCfg.btnCls}`}
          >
            {publishing ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                {t("Veröffentliche…", "Publishing…")}
              </>
            ) : done ? (
              <>
                <CheckCircle size={15} />
                {t("Veröffentlicht!", "Published!")}
              </>
            ) : (
              <>
                <Send size={15} />
                {t("Jetzt veröffentlichen", "Publish now")}
              </>
            )}
          </button>
        </div>
        )}
      </div>

      {/* inline keyframe für den Dialog-Eingang */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
